import { Component, effect, inject, Injector, input, OnInit, signal, untracked } from '@angular/core';
import { Validators } from '@angular/forms';

import { ButtonModule, DialogRef, DialogService } from '@hglobal/fin-ui';
import { FormBuilder, FormGroup } from '@ngneat/reactive-forms';
import { GroupResolverFormBuilder } from '@ngneat/reactive-forms/lib/form-builder';
import { TranslateModule } from '@ngx-translate/core';
import { v4 as uuid } from 'uuid';

import {
  ExchangeCryptoDetailsComponent,
  ExchangeFailedComponent,
  ExchangeSetCryptoCryptoComponent,
  ExchangeSuccessComponent,
} from '@kr/components/exchange/components';
import { ExchangeStore } from '@kr/components/exchange/exchange.store';
import { WalletCard } from '@kr/models';

enum ExchangeCryptoCryptoFlow {
  Set = 'set',
  Details = 'details',
  Success = 'success',
  Failed = 'failed',
}

@Component({
  selector: 'kr-crypto-crypto-flow',
  imports: [
    ButtonModule,
    ExchangeCryptoDetailsComponent,
    ExchangeFailedComponent,
    ExchangeSetCryptoCryptoComponent,
    ExchangeSuccessComponent,
    TranslateModule,
  ],
  templateUrl: './crypto-crypto-flow.component.html',
  styleUrl: './crypto-crypto-flow.component.scss',
})
export class CryptoCryptoFlowComponent implements OnInit {
  readonly #fb = inject(FormBuilder);
  readonly #store = inject(ExchangeStore);
  readonly #dialogService = inject(DialogService);
  readonly #injector = inject(Injector);
  readonly dialogRef = inject(DialogRef);
  readonly Number = Number;

  form: FormGroup<GroupResolverFormBuilder<any>>;
  exchange = ExchangeCryptoCryptoFlow;
  page = signal<ExchangeCryptoCryptoFlow>(this.exchange.Set);
  estimation = this.#store.exchangeEstimate;
  timeLeft = signal(0);

  wallet = input<WalletCard>();
  wallets = input<WalletCard[]>();

  constructor() {
    effect(() => {
      const isSuccess = this.#store.isExchangeSuccess();
      const isFail = this.#store.exchangeError();
      const isPageOpened = location.pathname.includes('exchange');

      if (isSuccess) {
        untracked(() => {
          if (isPageOpened) {
            this.page.set(this.exchange.Set);
            this.#dialogService.open(ExchangeSuccessComponent, null, { parentInjector: this.#injector });
          } else {
            this.page.set(this.exchange.Success);
          }
        });
      }

      if (isFail) {
        untracked(() => {
          if (isPageOpened) {
            this.page.set(this.exchange.Set);
            this.#dialogService.open(ExchangeFailedComponent, null, { parentInjector: this.#injector });
          } else {
            this.page.set(this.exchange.Failed);
          }
        });
      }
    });
  }

  ngOnInit(): void {
    this.form = this.#fb.group<any>(
      {
        walletFromId: [this.wallet().id, Validators.required],
        walletToId: [<never>null, [Validators.required]],
        amount: [<never>null],
        amountTo: [<never>null],
      },
      { updateOn: 'blur' },
    );

    this.#store.saveForm(this.form);
    this.#store.saveWallets(this.wallets);
  }

  setStep(i: ExchangeCryptoCryptoFlow): void {
    const isFormInValid = this.form.invalid;
    const amount = Number(this.form.value['amount']?.replaceAll(',', ''));
    const amountTo = Number(this.form.value['amountTo']?.replaceAll(',', ''));

    if (isFormInValid || !(amount > 0 || amountTo > 0)) {
      return;
    }

    this.page.set(i);
  }

  execute() {
    this.#store.sendExchange({
      estimationId: this.estimation().estimationId,
      requestId: uuid(),
    });
  }
}
